import React from "react";

// Extend CSSProperties to include CSS custom properties
interface CSSPropertiesWithVars extends React.CSSProperties {
  "--button-bg"?: string;
}

interface AppModalAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "danger";
}

type GameVariant = "success" | "warning" | "info" | "error" | "default";
type RewardRarity = "common" | "uncommon" | "rare" | "epic" | "legendary" | "mythic";

interface AppModalProps {
  show: boolean;
  variant?: GameVariant | "reward";
  rarity?: RewardRarity;
  title: string;
  children: React.ReactNode;
  actions?: AppModalAction[];
}

// Rarity color constants
const RARITY_COLORS = {
  common: "#E5E7EB",
  uncommon: "#10B981",
  rare: "#3B82F6",
  epic: "#8B5CF6",
  legendary: "#F59E0B",
  mythic: "#EC4899"
};

// Game state modal styles - neutral backgrounds with color accents
const variantStyles = {
  success: {
    container: "bg-gray-800 border-2 border-green-500 text-gray-100",
    title: "text-gray-100",
    primary: "bg-green-600 hover:bg-green-700",
    headerLabel: "MATCH COMPLETE"
  },
  warning: {
    container: "bg-gray-800 border-2 border-amber-500 text-gray-100",
    title: "text-gray-100",
    primary: "bg-amber-600 hover:bg-amber-700",
    headerLabel: "ATTENTION"
  },
  info: {
    container: "bg-gray-800 border-2 border-blue-500 text-gray-100",
    title: "text-gray-100",
    primary: "bg-blue-600 hover:bg-blue-700",
    headerLabel: "GAME UPDATE"
  },
  error: {
    container: "bg-gray-800 border-2 border-red-500 text-gray-100",
    title: "text-gray-100",
    primary: "bg-red-600 hover:bg-red-700",
    headerLabel: "ERROR"
  },
  default: {
    container: "bg-gray-800 border-2 border-gray-500 text-gray-100",
    title: "text-gray-100",
    primary: "bg-gray-600 hover:bg-gray-700",
    headerLabel: "NOTIFICATION"
  }
};

// Reward modal styles - full color treatments with gradients
const rarityStyles = {
  common: {
    background: "linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)",
    borderColor: RARITY_COLORS.common,
    buttonBg: "#6B7280",
    textColor: "#1F2937",
    glowColor: "rgba(229, 231, 235, 0.3)"
  },
  uncommon: {
    background: "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)",
    borderColor: RARITY_COLORS.uncommon,
    buttonBg: RARITY_COLORS.uncommon,
    textColor: "#064E3B",
    glowColor: "rgba(16, 185, 129, 0.3)"
  },
  rare: {
    background: "linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)",
    borderColor: RARITY_COLORS.rare,
    buttonBg: RARITY_COLORS.rare,
    textColor: "#1E3A8A",
    glowColor: "rgba(59, 130, 246, 0.3)"
  },
  epic: {
    background: "linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)",
    borderColor: RARITY_COLORS.epic,
    buttonBg: RARITY_COLORS.epic,
    textColor: "#4C1D95",
    glowColor: "rgba(139, 92, 246, 0.3)"
  },
  legendary: {
    background: "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)",
    borderColor: RARITY_COLORS.legendary,
    buttonBg: RARITY_COLORS.legendary,
    textColor: "#78350F",
    glowColor: "rgba(245, 158, 11, 0.4)"
  },
  mythic: {
    background: "linear-gradient(135deg, #FFEEF5 0%, #FFDDED 100%)",
    borderColor: RARITY_COLORS.mythic,
    buttonBg: RARITY_COLORS.mythic,
    textColor: "#831843",
    glowColor: "rgba(236, 72, 153, 0.5)"
  }
};

const actionStyles = {
  primary: "text-white font-semibold",
  secondary: "bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium",
  danger: "bg-red-600 hover:bg-red-700 text-white font-semibold"
};

export function AppModal({
  show,
  variant = "default",
  rarity = "common",
  title,
  children,
  actions
}: AppModalProps) {
  if (!show) return null;

  // Reward modal rendering
  if (variant === "reward") {
    const style = rarityStyles[rarity];

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
        <div
          className={`reward-modal reward-modal__container reward-modal--${rarity} ${rarity === "legendary" ? "reward-modal-glow" : ""} pointer-events-auto`}
          style={
            {
              color: style.textColor,
              "--button-bg": style.buttonBg
            } as CSSPropertiesWithVars
          }
        >
          <div className="reward-modal__header">REWARD EARNED!</div>

          <h2 className="reward-modal__title">{title}</h2>

          <div className="reward-modal__content">{children}</div>

          {actions && actions.length > 0 && (
            <div className="reward-modal__actions">
              {actions.map((action, index) => {
                const isPrimary = action.variant === "primary" || index === 0;

                return (
                  <button
                    key={index}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    className={`reward-modal__button ${
                      isPrimary
                        ? "reward-modal__button--primary"
                        : "reward-modal__button--secondary"
                    }`}
                  >
                    {action.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Shine effect overlay for non-common rarities */}
          {rarity !== "common" && <div className="reward-modal__shine-overlay" />}

          {/* Shimmer effect for mythic rarity */}
          {rarity === "mythic" && <div className="reward-modal__shimmer-overlay" />}
        </div>
      </div>
    );
  }

  // Game state modal rendering (existing variants)
  const styles = variantStyles[variant as GameVariant] || variantStyles.default;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
      <div
        className={`${styles.container} rounded-lg shadow-2xl p-6 max-w-md w-full mx-4 pointer-events-auto`}
      >
        <h2 className={`text-2xl font-bold mb-3 text-center ${styles.title}`}>{title}</h2>

        <div className="text-lg text-center">{children}</div>

        {actions && actions.length > 0 && (
          <div className="flex justify-center gap-3 mt-5">
            {actions.map((action, index) => {
              const actionVariant = action.variant || (index === 0 ? "primary" : "secondary");
              const baseStyles =
                "px-6 py-2 rounded-lg shadow-md transition-all duration-200 disabled:opacity-50";
              const variantSpecificStyles =
                actionVariant === "primary"
                  ? `${styles.primary} ${actionStyles.primary}`
                  : actionVariant === "danger"
                    ? actionStyles.danger
                    : actionStyles.secondary;

              return (
                <button
                  key={index}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className={`${baseStyles} ${variantSpecificStyles} hover:-translate-y-0.5 active:translate-y-0`}
                >
                  {action.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Reward content display component - kept in same file for simplicity
interface RewardContentProps {
  type: string;
  amount: number;
  rarity: RewardRarity;
  icon?: string;
  description?: string;
}

export function RewardContent({
  type,
  amount,
  rarity,
  icon = "🎁",
  description
}: RewardContentProps) {
  const rarityLabels = {
    common: "COMMON",
    uncommon: "UNCOMMON",
    rare: "RARE",
    epic: "EPIC",
    legendary: "LEGENDARY",
    mythic: "MYTHIC"
  };

  return (
    <div className="reward-content">
      {/* Icon on left */}
      <div className="reward-content__icon">{icon}</div>

      {/* Content on right */}
      <div className="reward-content__details">
        <div className="reward-content__label">{rarityLabels[rarity]} REWARD</div>
        <div className="reward-content__type">{type}</div>
        <div className="reward-content__amount">x{amount.toLocaleString()}</div>
        {description && <div className="reward-content__description">{description}</div>}
      </div>
    </div>
  );
}
